// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
// @todo: Migrate to 'Dialog.Modal' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Dialog from '@wandb/weave/components/Dialog';
import {maybePluralize} from '@wandb/weave/core/util/string';
import {clsx} from 'clsx';
import React, {useContext, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {
  StarredRegistriesDocument,
  useDeleteModelMutation,
} from '../../../generated/graphql';
import {
  doNotRetryContext,
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../../util/errors';
import * as urls from '../../../util/urls';
import {RegistriesSearchContext} from '../common/RegistriesSearchContextProvider';
import {globalRegistryType} from '../common/utils';
import {FETCH_REGISTRY_PROJECTS_PARTIAL} from '../graphql/registryProjects.query';
import {FETCH_REGISTRIES_PAGE_SIZE} from '../RegistriesHomepage/hooks';

const DARK_INPUT_STYLE =
  'dark:border-moon-750 dark:bg-moon-900 dark:placeholder:text-moon-500';
const LIGHT_INPUT_STYLE = 'border-moon-250 placeholder:text-moon-500';
const INPUT_STYLE = 'mt-8 h-40 w-full rounded border px-12 py-8 outline-none';

export interface DeleteRegistryModalProps {
  isOpen: boolean;
  registry: globalRegistryType;
  setIsModalOpen: (isOpen: boolean) => void;
  handleModalClose(): void;
}

export const DeleteRegistryModal = ({
  isOpen,
  registry,
  setIsModalOpen,
  handleModalClose,
}: DeleteRegistryModalProps) => {
  const {id: registryId, orgName} = registry;
  const history = useHistory();
  const {searchFilter, searchPattern} = useContext(RegistriesSearchContext);

  const [input, setInput] = useState('');

  const [deleteRegistry, deleteRegistryStatus] = useDeleteModelMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    refetchQueries: [
      {
        query: FETCH_REGISTRY_PROJECTS_PARTIAL,
        variables: {
          orgName,
          pattern: searchPattern,
          limit: FETCH_REGISTRIES_PAGE_SIZE,
        },
      },
      {
        query: StarredRegistriesDocument,
        variables: {
          organizationId: registry.orgId,
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const deleteButtonEnabled = useMemo(
    () =>
      input === registry.registryName && deleteRegistryStatus.loading !== true,
    [input, deleteRegistryStatus.loading, registry.registryName]
  );

  const onDeleteClick = async () => {
    try {
      await deleteRegistry({variables: {id: registryId}});
      toast('Registry has been deleted!');
      handleModalClose();
      // return back to the registries homepage with the same search filter
      history.push(
        urls.globalRegistry(
          orgName,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          false,
          searchFilter
        )
      );
    } catch (e) {
      console.error(
        'Error trying to delete project: ',
        extractErrorMessageFromApolloError(e)
      );
      toast('Error occured while trying to delete project.');
    }
  };
  const onOpenChange = (open: boolean) => {
    if (!deleteRegistryStatus.loading) {
      setIsModalOpen(open);
      setInput('');
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          className="w-[600px] rounded-lg"
          onInteractOutside={e => {
            e.preventDefault();
          }}>
          <Dialog.Title>Delete {registry.registryName} registry?</Dialog.Title>
          <Dialog.Description className="pt-16" data-test="delete-summary">
            The {maybePluralize(registry.collectionCount, 'collection')} of the
            registry "{registry.registryName}" will also be deleted.
          </Dialog.Description>
          <Dialog.Description className="pt-16">
            Please type <strong>the registry name</strong> to confirm.
          </Dialog.Description>
          <input
            data-test="delete-all-input"
            className={clsx(INPUT_STYLE, LIGHT_INPUT_STYLE, DARK_INPUT_STYLE)}
            placeholder={registry.registryName}
            onChange={e => setInput(e.target.value)}
          />
          <div className="mb-8 mt-24 flex items-center">
            <Button
              data-test="delete-all-button"
              variant={deleteButtonEnabled ? 'destructive' : 'secondary'}
              disabled={!deleteButtonEnabled}
              size="large"
              onClick={onDeleteClick}
              className={
                deleteButtonEnabled
                  ? ''
                  : 'dark:bg-moonbeam/[.03] dark:text-moon-650 dark:opacity-100 dark:filter-none'
              }>
              Delete
            </Button>
            <Dialog.Close className="ml-8">
              <Button
                className="dark:text-moon-200/100 dark:filter-none hover:dark:text-moon-200"
                variant="ghost"
                size="large"
                disabled={deleteRegistryStatus.loading}>
                Cancel
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
